import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isMfaRequired,
  type Capability,
  type MeResponse,
  type MfaChallengeView,
  type MfaRequired,
  type SessionOrg,
} from '@simpleacs/shared';
import { api, ApiRequestError } from './api';

interface SessionValue {
  session: MeResponse | null;
  isLoading: boolean;
  /**
   * Signs in, or reports that a second factor is needed.
   *
   * Returns the challenge rather than throwing, because "your password was
   * right and there is one more step" is a success from the caller's point of
   * view — the login form has to render a different field, not an error.
   */
  login: (email: string, password: string) => Promise<MfaChallengeView | null>;
  /** Answers a challenge with the sent code or a recovery code. */
  answerMfa: (token: string, code: string) => Promise<void>;
  /** Another code for the same challenge. */
  resendMfa: (token: string) => Promise<MfaChallengeView>;
  logout: () => Promise<void>;
  switchOrg: (orgId: string | null) => Promise<void>;
  can: (capability: Capability) => boolean;
  activeOrg: SessionOrg | null;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return await api.get<MeResponse>('/auth/me');
      } catch (err) {
        // Not being logged in is a normal state, not a query failure.
        if (err instanceof ApiRequestError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  /**
   * A login now answers one of two things, and only one of them is a session.
   *
   * The cache is written ONLY for the authenticated shape. Storing a challenge
   * under `['session']` would make every `can()` in the app read a half-login
   * as a signed-in user, which is the exact confusion the challenge exists to
   * prevent — and the cookie does not exist yet either, so the next request
   * would 401 anyway.
   */
  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<MeResponse | MfaRequired>('/auth/login', input),
    onSuccess: (data) => {
      if (!isMfaRequired(data)) queryClient.setQueryData(['session'], data);
    },
  });

  const mfaMutation = useMutation({
    mutationFn: (input: { token: string; code: string }) =>
      api.post<MeResponse>('/auth/login/mfa', input),
    onSuccess: (data) => queryClient.setQueryData(['session'], data),
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>('/auth/logout'),
    onSuccess: () => {
      queryClient.setQueryData(['session'], null);
      queryClient.clear();
    },
  });

  const switchOrgMutation = useMutation({
    mutationFn: (orgId: string | null) =>
      api.post<{ activeOrg: SessionOrg | null }>('/auth/acting-org', { orgId }),
    onSuccess: async () => {
      // Every tenant-scoped query is now stale.
      await queryClient.invalidateQueries();
    },
  });

  const session = meQuery.data ?? null;

  const can = useCallback(
    (capability: Capability) => session?.capabilities.includes(capability) ?? false,
    [session],
  );

  const value = useMemo<SessionValue>(
    () => ({
      session,
      isLoading: meQuery.isLoading,
      login: async (email, password) => {
        const result = await loginMutation.mutateAsync({ email, password });
        return isMfaRequired(result) ? result.challenge : null;
      },
      answerMfa: async (token, code) => {
        await mfaMutation.mutateAsync({ token, code });
      },
      resendMfa: (token) => api.post<MfaChallengeView>('/auth/login/mfa/resend', { token }),
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
      switchOrg: async (orgId) => {
        await switchOrgMutation.mutateAsync(orgId);
      },
      can,
      activeOrg: session?.activeOrg ?? null,
    }),
    [
      session,
      meQuery.isLoading,
      loginMutation,
      mfaMutation,
      logoutMutation,
      switchOrgMutation,
      can,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
